import { Dialog } from "@headlessui/react"
import { KTitleSpan, KSpan, KButton, KInput } from "kahuna-base-react-components"
import React, { useEffect, useState } from "react"
import { User, SmartLinkArtist, SelectOption, ArtistSearch, Artist } from "../../../../../types"
import { X } from "heroicons-react"
import { featureFm, releases } from "../../../../../apis"
import { mapListToCamelCase, mapToCamelCase, mapToSnakeCase } from "../../../../../utility"
import { toast } from "react-toastify"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"

const initialSmartLinkArtistForm: SmartLinkArtist = {
  artistName: "",
  countryCode: "",
  spotifyArtistUrl: ""
}

const AdminSmartLinkArtistModal = ({
  open,
  onClose,
  user,
  isModal
}: {
  open: boolean
  onClose: () => void
  user: User
  isModal: boolean
}) => {
  const [smartLinkArtistList, setSmartLinkArtistList] = useState<SmartLinkArtist[]>([])
  const [smartLinkArtist, setSmartLinkArtist] = useState<SmartLinkArtist>(initialSmartLinkArtistForm)
  const [addNewOpen, setAddNewOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [timeoutId, setTimeoutId] = useState(null)
  const [artistOptions, setArtistOptions] = useState<SelectOption[]>([])
  const [searchText, setSearchText] = useState<string>("")
  const [spotifySearchLoading, setSpotifySearchLoading] = useState<boolean>(false)

  const fetchSmartLinkArtistList = () => {
    featureFm(`/smart_link_artists/?user_id=${user.id}`, "GET").then((resp) => {
      const smartLinkArtists: SmartLinkArtist[] = mapListToCamelCase(resp.data.results)
      setSmartLinkArtistList(smartLinkArtists)
    })
  }

  useEffect(() => {
    fetchSmartLinkArtistList()
  }, [])

  const onSave = (smartLinkArtist: SmartLinkArtist) => {
    if (!smartLinkArtist.countryCode || !smartLinkArtist.artistName || !smartLinkArtist.spotifyArtistUrl) {
      toast.error("Empty fields", errorFilledToastOptions)
      return
    }
    if (smartLinkArtist.countryCode.length !== 2) {
      toast.error("Country code must be 2 characters", errorFilledToastOptions)
      return
    }
    if (!smartLinkArtist.spotifyArtistUrl.startsWith("https://open.spotify.com/artist/")) {
      toast.error("Spotify Artist URL must start with https://open.spotify.com/artist/", errorFilledToastOptions)
      return
    }
    setLoading(true)
    featureFm(`/save_smart_link_artist/?user_id=${user.id}`, "POST", { ...mapToSnakeCase(smartLinkArtist) })
      .then((resp) => {
        setLoading(false)
        if (resp.data.result) {
          toast.success("Smart link artist saved successfully", successFilledToastOptions)
          if (smartLinkArtist.id) {
            setSmartLinkArtistList(
              smartLinkArtistList.map((sla) => (sla.id === smartLinkArtist.id ? { ...smartLinkArtist } : sla))
            )
          } else {
            const result = mapToCamelCase(resp.data.result)
            smartLinkArtist.id = result.id
            smartLinkArtist.featureFmId = result.featureFmId
            setSmartLinkArtistList([smartLinkArtist, ...smartLinkArtistList])
            setAddNewOpen(false)
          }
          setSmartLinkArtist(initialSmartLinkArtistForm)
        } else {
          toast.error(`Error: ${resp.data.message}`, errorFilledToastOptions)
        }
      })
      .catch(() => {
        setLoading(false)
        toast.error("Failed to save smart link artist", errorFilledToastOptions)
      })
  }

  const onEdit = (smartLinkArtist: SmartLinkArtist) => {
    setAddNewOpen(false)
    setSmartLinkArtist(smartLinkArtist)
  }

  const onSearchArtist = (search: string) => {
    setSearchText(search)
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    setSpotifySearchLoading(true)

    setTimeoutId(
      // @ts-ignore
      setTimeout(async () => {
        if (!search) {
          setSpotifySearchLoading(false)
          setArtistOptions([])
          return
        }
        releases(`/artist/search/?value=${search}&sp_enabled=1`, "get")
          .then((resp) => {
            setSpotifySearchLoading(false)
            if (resp?.data?.result) {
              const artistSearch: ArtistSearch = mapToCamelCase(resp.data.result)
              const spotifyOptions: SelectOption[] = artistSearch.spotify.map((option: Artist) => {
                return {
                  value: option.name,
                  label: option.name,
                  spotifyId: option.spotifyId,
                  cover: option.cover || "/reports_icons/avatar.svg"
                }
              })

              setArtistOptions(spotifyOptions)
            } else {
              setArtistOptions([])
            }
          })
          .catch(() => {
            setSpotifySearchLoading(false)
            setArtistOptions([])
          })
      }, 1000)
    )
  }

  const onCancel = () => {
    setAddNewOpen(false)
    setSmartLinkArtist(initialSmartLinkArtistForm)
  }

  /*
  const onDelete = (id?: number) => {
    if (!id) {
      const confirmation = window.confirm("Are you sure you want to discard changes?")
      if (confirmation) {
        setSmartLinkArtist(initialSmartLinkArtistForm)
        setAddNewOpen(false)
      }
      return
    } else {
      const confirmation = window.confirm("Are you sure you want to DELETE?")
      if (!confirmation) {
        return
      }
    }

    setLoading(true)
    featureFm(`/delete_smart_link_artist/?user_id=${user.id}&id=${id}`, "DELETE")
      .then((resp) => {
        setLoading(false)
        if (resp.data.success) {
          toast.success("Smart link artist deleted successfully", successFilledToastOptions)
          setSmartLinkArtistList(smartLinkArtistList.filter((sla) => sla.id !== id))
        } else {
          toast.error(`Error: ${resp.data.message}`, errorFilledToastOptions)
        }
      })
      .catch(() => {
        setLoading(false)
        toast.error("Failed to delete smart link artist", errorFilledToastOptions)
      })
  }*/

  const renderSearchSpotify = () => {
    return (
      <div className="mb-3">
        <div>
          <KSpan text="Search for a Spotify artist or enter the details manually below" />
        </div>
        <div className="mt-1">
          <KInput placeholder="Search..." onChange={(value) => onSearchArtist(value)} value={searchText} />
        </div>
        <div>
          {spotifySearchLoading && (
            <div className="mt-1">
              <KSpan text="Searching..." />
            </div>
          )}
          {artistOptions.length > 0 && (
            <div className="mt-1 flex flex-col">
              {artistOptions.map((option) => (
                <div
                  key={option.value}
                  className="mt-1 flex gap-2 items-center hover:bg-gray-100 cursor-pointer pt-1 pb-1 pr-2 pl-2 rounded"
                  onClick={() => {
                    setSmartLinkArtist({
                      artistName: option.value?.toString() || "",
                      artistImage: option.cover,
                      countryCode: "",
                      spotifyArtistUrl: `https://open.spotify.com/artist/${option.spotifyId}`
                    })
                    setArtistOptions([])
                  }}
                >
                  <img src={option.cover} style={{ width: 40, borderRadius: 5 }} />
                  <KSpan text={option.value?.toString() || ""} fontSize={16} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderForm = (sla: SmartLinkArtist) => {
    if (sla.id !== smartLinkArtist.id) {
      return (
        <div className="flex justify-between" key={`sla_view_${sla.id}`} style={{ borderTop: "1px solid #9b9b9b" }}>
          <div className="flex flex-col gap-2 pt-2 pb-2">
            <div className="flex gap-2">
              <img
                src={sla.artistImage || "/admin_icons/user.svg"}
                style={{ width: 46, height: 46, borderRadius: 5 }}
              />
              <div className="flex flex-col gap-2">
                <KSpan text={`Artist Name: ${sla.artistName}`} fontSize={14} lineHeight="20px" color="#000" />
                <KSpan text={`Country Code: ${sla.countryCode}`} fontSize={14} lineHeight="20px" color="#000" />
              </div>
            </div>
            <KSpan text={`Spotify Artist URL: ${sla.spotifyArtistUrl}`} fontSize={14} lineHeight="20px" color="#000" />
          </div>

          <div className="flex flex-col items-center justify-center ml-20">
            <div className="flex flex-row gap-4 mr-2">
              <KButton background="transparent" onClick={() => onEdit(sla)} icon="/account_icons/edit.svg" />
            </div>
          </div>
        </div>
      )
    }

    return (
      <div
        className="flex justify-between pt-2 pb-2"
        key={`sla_edit_${sla.id}`}
        style={{ borderTop: "1px solid #9b9b9b" }}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2" style={{ width: "100%" }}>
            <KInput
              width={350}
              value={smartLinkArtist.artistName || ""}
              onChange={(value) => setSmartLinkArtist({ ...smartLinkArtist, artistName: value })}
              placeholder="Artist Name"
            />
            <KInput
              width={120}
              value={smartLinkArtist.countryCode || ""}
              onChange={(value) =>
                value.length <= 2 && setSmartLinkArtist({ ...smartLinkArtist, countryCode: value.toUpperCase() })
              }
              placeholder="Country (XX)"
            />
          </div>
          <div>
            <KInput
              width={470}
              value={smartLinkArtist.spotifyArtistUrl || ""}
              onChange={(value) => setSmartLinkArtist({ ...smartLinkArtist, spotifyArtistUrl: value })}
              placeholder="Spotify Artist URL (https://open.spotify.com/artist/xxxxx)"
            />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center ml-20 gap-4 mr-2">
            <KButton
              disabled={loading}
              background="black"
              textColor="white"
              onClick={() => onSave(smartLinkArtist)}
              text="Save"
            />
            <KButton
              disabled={loading}
              background="transparent"
              icon="/account_icons/cancel.svg"
              onClick={() => onCancel()}
            />
          </div>
        </div>
      </div>
    )
  }

  const renderMain = () => {
    return (
      <div
        className="rounded-[10px] bg-[#fff]"
        style={{ minWidth: 540 }}
        onKeyDown={(e) => e.key === "Escape" && setSmartLinkArtist(initialSmartLinkArtistForm)}
      >
        <div className={`flex justify-end`} data-testid="admin-user-create-cancel-button">
          <X className="h-4 cursor-pointer mt-1" style={{ position: "absolute" }} onClick={() => onClose()} />
        </div>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-6 items-center justify-center">
            <span
              className="w-[72px] aspect-square rounded-full flex justify-center items-center"
              style={{
                border: "1px solid #F3F3F3"
              }}
            >
              <img className="w-8 h-8" src="/analytics_icons/link.svg" />
            </span>
            <KTitleSpan text={"Smart Link - Artist"} fontSize={20} lineHeight="28px" color="#000" />
          </div>
        </div>
        {!addNewOpen && (
          <div className={`flex justify-start mb-3 mt-3`} data-testid="feature-fm-add-new-button">
            <KButton
              background="transparent"
              onClick={() => {
                setSmartLinkArtist(initialSmartLinkArtistForm)
                setAddNewOpen(true)
              }}
              text="+ Add New"
            />
          </div>
        )}

        <div className="flex flex-col" style={{ maxHeight: "68vh", overflowY: "auto" }}>
          {addNewOpen && renderSearchSpotify()}
          {addNewOpen && <div className="mb-4">{renderForm(smartLinkArtist)}</div>}
          {smartLinkArtistList.map((sla) => renderForm(sla))}
        </div>
      </div>
    )
  }

  return isModal ? (
    <Dialog
      open={open}
      onClose={() => {
        onClose()
      }}
    >
      <div className="fixed w-screen h-screen top-0 left-0 z-250 flex items-center justify-center bg-[#0000004d] ">
        {renderMain()}
      </div>
    </Dialog>
  ) : (
    renderMain()
  )
}

export default AdminSmartLinkArtistModal
